// A SHOP IN HIPAA MODE'S FORM (2026-10-08; docs/HIPAA.md → What changes for a shop on the plan; the API's hipaa_form.py is
// the whole of the rule). Two sections, drawn only for a shop the API says is in HIPAA mode (`/shop` answers `hipaa: true`;
// the static config does not carry it, so they appear the moment the API's answer lands, a second or two after the page):
//
//   THE NOTICE OF PRIVACY PRACTICES: the practice's own words (`public_data.privacy_notice`, {title, text, version}, written
//   by the API from what the owner pasted in Settings), shown before the signature with ONE box the client must tick,
//   "I received this practice's Notice of Privacy Practices". The form posts `privacyNotice: {received: true, version}`, and
//   the API refuses a signing without it (NPP_NOT_ACKNOWLEDGED), the terms box's shape.
//
//   THE PHOTO RELEASE: a HIPAA authorization, the shop's switch (`public_data.photo_release`, {years}), OFF by default. Its
//   own section, every element 164.508(c) asks for, and one box the client may leave alone: "Yes, I sign this photo
//   release." The form posts `photoRelease: {signed, years}`; a client who does not tick it signs the waiver all the same.
//
// And the ordinary form's portfolio clause ("I allow my tattoo to be photographed and be used for <shop>'s portfolio
// showcase") is not shown to a HIPAA shop's client and not posted: it is a photo use conditioned on the signing, which
// 164.508(b)(4) forbids a covered entity, so the release above is the one way a photo leaves the client's care. This module is
// the only place that decides what the keys mean; the rest of the form asks it.

import type { Strings } from "./i18n";

export type PrivacyNotice = { title: string; text: string; version: string };
export type PhotoRelease = { years: number };

/** The periods the API offers (hipaa_form.RELEASE_YEARS). */
export const RELEASE_YEARS = [1, 2, 3, 5, 10];

/** The ordinary form's portfolio clause, which a HIPAA shop's client never sees and never posts. */
export const PORTFOLIO_CLAUSE = "photograph";

type ShopLike = { hipaa?: boolean; public_data?: { privacy_notice?: unknown; photo_release?: unknown } | null } | null | undefined;

const VERSION = /^[0-9a-f]{64}$/;

/** The notice, for a shop in HIPAA mode that set one; null for every other shop and anything that is not one. */
export function privacyNoticeOf(shop: ShopLike): PrivacyNotice | null {
  if (shop?.hipaa !== true) return null;
  const raw = shop.public_data?.privacy_notice;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const text = typeof r.text === "string" ? r.text.trim() : "";
  const version = typeof r.version === "string" ? r.version.trim() : "";
  if (!text || !VERSION.test(version)) return null;
  const title = typeof r.title === "string" && r.title.trim() ? r.title.trim() : "Notice of Privacy Practices";
  return { title, text, version };
}

/** The release, for a shop in HIPAA mode that turned it on; null otherwise. */
export function photoReleaseOf(shop: ShopLike): PhotoRelease | null {
  if (shop?.hipaa !== true) return null;
  const raw = shop.public_data?.photo_release;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const years = Number((raw as Record<string, unknown>).years);
  return RELEASE_YEARS.includes(years) ? { years } : null;
}

/** Does this shop's form show the ordinary portfolio clause? Every shop's but a HIPAA shop's. */
export const showsPortfolioClause = (shop: ShopLike): boolean => shop?.hipaa !== true;

/** The release as the client reads it, the shop's name and the period filled in, in the language on screen. */
export function releaseWords(T: Strings, shopName: string, years: number): { heading: string; lines: string[]; box: string } {
  const fill = (text: string) => text.split("{shop}").join(shopName).split("{years}").join(T.yearsWords(years));
  return { heading: fill(T.photoReleaseHeading), lines: T.photoReleaseLines.map(fill), box: fill(T.photoReleaseBox) };
}
